import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Divider, List, Space } from "antd";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import useAiAssessmentStart from "../../hooks/useAiAssessmentStart";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import useAiAssessmentViolationCapture from "../../hooks/useAiAssessmentViolationCapture";
import { CHART_COLORS } from "../../../../Utils/chartColors";
import "./modals.scss";

const FullScreenExitModal: React.FC = () => {
  const {
    showExitFullScreenModal,
    setShowExitFullScreenModal,
    fullscreenElementRef,
    isFullscreen,
    exitFullScreenTimer,
    setExitFullScreenTimer,
  } = useAIAssessmentContext();

  const { toggleToFullscreenMode } = useAiAssessmentStart();
  const [countdown, setCountdown] = useState(10);
  const [isRestoringFullscreen, setIsRestoringFullscreen] = useState(false);
  const { captureFullScreenExit } = useAiAssessmentViolationCapture();

  const handleStayInFullscreen = async () => {
    // Prevent multiple clicks
    if (isRestoringFullscreen) return;

    setIsRestoringFullscreen(true);
    setExitFullScreenTimer(false); // Stop the countdown

    try {
      // Attempt fullscreen WHILE modal is still open (preserves user gesture)
      const success = await toggleToFullscreenMode();

      if (success) {
        // Only close modal after successful fullscreen restoration
        setShowExitFullScreenModal(false);
      } else {
        // Fullscreen failed - restart timer and let user try again
        setExitFullScreenTimer(true);
      }
    } catch (error) {
      console.error("Failed to restore fullscreen:", error);
      setExitFullScreenTimer(true);
    } finally {
      setIsRestoringFullscreen(false);
    }
  };

  const handleConfirmExit = async () => {
    setShowExitFullScreenModal(false);
    setExitFullScreenTimer(false);
    await captureFullScreenExit();
  };

  // Intentional quit - candidate chooses to leave the assessment
  const handleQuitAssessment = async () => {
    setShowExitFullScreenModal(false);
    setExitFullScreenTimer(false);
    await captureFullScreenExit();
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (exitFullScreenTimer && showExitFullScreenModal) {
      setCountdown(10);
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            handleConfirmExit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [exitFullScreenTimer, showExitFullScreenModal]);

  return (
    <Modal
      closeIcon={false}
      destroyOnClose={true}
      centered
      open={showExitFullScreenModal}
      onCancel={handleStayInFullscreen}
      maskClosable={false}
      footer={null}
      keyboard={false}
      className="fullscreen-exit-modal"
      // Fully opaque black mask to hide the non-fullscreen view
      styles={{ mask: { backgroundColor: "rgba(0, 0, 0, 1)" } }}
      getContainer={
        isFullscreen ? () => fullscreenElementRef.current! : undefined
      }
    >
      <div className="modal-content">
        <div className="modal-content-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Fullscreen Exit Detected !!
          </Typography.Title>
        </div>
        <Divider style={{ margin: 0 }} />
        <div className="violation-modal-content-icon">
          <Typography.Title level={1} style={{ margin: 0, color: CHART_COLORS.error }}>
            {countdown}
          </Typography.Title>
        </div>

        <List size="small" style={{ width: "100%" }}>
          <List.Item>
            <List.Item.Meta
              avatar={<WarningOutlined style={{ color: CHART_COLORS.warning }} />}
              title="Fullscreen exit has been detected during the AI Assessment Round."
            />
          </List.Item>
          <List.Item>
            <List.Item.Meta
              avatar={<ExclamationCircleOutlined style={{ color: CHART_COLORS.error }} />}
              title="If you exit fullscreen mode, you will be disqualified from the interview."
            />
          </List.Item>

          <List.Item>
            <List.Item.Meta
              avatar={<CheckCircleOutlined style={{ color: CHART_COLORS.success }} />}
              title="If you stay in fullscreen mode, you will be able to continue the interview."
            />
          </List.Item>
        </List>
      </div>
      <div className="ai-assessment-modal-footer">
        <Space>
          <Button
            danger
            icon={<LogoutOutlined />}
            onClick={handleQuitAssessment}
            disabled={isRestoringFullscreen}
          >
            Quit Assessment
          </Button>
          <Button
            type="primary"
            onClick={handleStayInFullscreen}
            loading={isRestoringFullscreen}
          >
            {isRestoringFullscreen ? "Restoring..." : "Stay in Fullscreen"}
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default FullScreenExitModal;
